import type { ITotalData } from '@/api/types/cart';
import { IS_US } from '@/utils/constants';
import type {
  AppliedOfferSource,
  AutoOfferApiParams,
  AutoOfferEnableInput,
  AutoOfferStore,
  NormalizedAppliedOffer,
} from './types';

export type {
  AppliedOfferSource,
  AutoOfferApiParams,
  AutoOfferCouponMode,
  AutoOfferEnableInput,
  AutoOfferPointMode,
  AutoOfferStore,
  NormalizedAppliedOffer,
} from './types';

export const PAYMENT_AUTO_OFFER_TOAST_STORAGE_KEY = 'zl-payment-auto-offer-toast';

export const normalizeAmount = (amount?: string | number | null) => {
  if (amount === null || amount === undefined || amount === '') {
    return 0;
  }
  const value = Number(String(amount).replace(/,/g, ''));
  return Number.isFinite(value) ? value : 0;
};

export const shouldEnableManualOffer = ({ selectedCount = 0 }: AutoOfferEnableInput) =>
  IS_US && selectedCount > 0;

/** 自动资格只来自新实验，游客不能自动选券。 */
export const shouldAutoApplyOffer = (state?: AutoOfferStore) =>
  Boolean(state?.enabled && state.authenticated && state.couponPolicy === 'automatic');

/** 用户显式恢复、明确券失效或购物车确认的新入场可恢复默认券策略；积分保持独立。 */
export const clearAutoOfferCouponSelection = (state: AutoOfferStore): AutoOfferStore => ({
  ...state,
  couponMode: 'auto',
  coupon: undefined,
  code: '',
  couponText: '',
});

export const clearFailedCouponSelection = (
  state: AutoOfferStore,
  explicitlyInvalid = false,
): AutoOfferStore =>
  explicitlyInvalid && ['manual_coupon', 'manual_code'].includes(state.couponMode)
    ? clearAutoOfferCouponSelection(state)
    : state;

/** 网络错误不证明用户的券失效；只清结果，不清意图。 */
export const resolveCartOfferAfterPricingFailure = (
  state: AutoOfferStore,
  explicitlyInvalid = false,
): AutoOfferStore => ({
  ...clearFailedCouponSelection(state, explicitlyInvalid),
  applied_offer: normalizeAppliedOffer(undefined),
});

/**
 * 计价失败时，接口没有可靠的新金额可覆盖，因此保留上一轮金额；
 * 但优惠和赠品资格都属于本次计价结果，必须清空，避免继续展示旧资格。
 */
export const clearFailedCartPricingPresentation = (totalData: ITotalData): ITotalData => ({
  ...totalData,
  code_discount: '0.00',
  point_discount: '0.00',
  applied_offer: undefined,
  show_gift: undefined,
  cart_display_modules: undefined,
});

export const restoreAutoOfferState = clearAutoOfferCouponSelection;

/**
 * couponMode=auto 表示遵循当前分组的默认策略；实际自动选券仍受分组策略和登录资格约束。
 * 四组默认都不使用积分，用户主动选择积分后与券/码选择独立维护。
 */
export const createDefaultAutoOfferStore = (
  state?: Partial<Pick<AutoOfferStore, 'enabled' | 'couponPolicy' | 'authenticated'>>,
): AutoOfferStore => ({
  enabled: state?.enabled ?? IS_US,
  couponPolicy: state?.couponPolicy ?? 'manual',
  authenticated: state?.authenticated ?? false,
  couponMode: 'auto',
  pointMode: 'none',
  code: '',
  expendPoint: undefined,
  applied_offer: normalizeAppliedOffer(undefined),
  coupon: undefined,
});

export const hasExplicitAutoOfferSelection = (state?: AutoOfferStore | null) =>
  Boolean(state && (state.couponMode !== 'auto' || state.pointMode !== 'none'));

/** 结算区的最大优惠提示只服务于用户手动选择的券或优惠码。 */
export const hasManualCouponOrCodeSelection = (state?: AutoOfferStore | null) => {
  if (state?.couponMode === 'manual_coupon') {
    return Boolean(state.coupon);
  }

  return state?.couponMode === 'manual_code' && Boolean(state.code?.trim());
};

export const normalizeAppliedOffer = (raw: any): NormalizedAppliedOffer => ({
  enabled: Boolean(raw?.enabled),
  applied: Boolean(raw?.applied),
  autoApplied: Boolean(raw?.auto_applied),
  show_best_offer_text: Boolean(raw?.show_best_offer_text),
  coupon: {
    source: (raw?.coupon?.source || 'none') as AppliedOfferSource,
    is_best: Boolean(raw?.coupon?.is_best),
    // 自动选券结果需携带真实用户券 ID 到支付快照；旧接口缺失时仍由既有手动选择兜底。
    ...(Number.isSafeInteger(raw?.coupon?.ticket_id) && raw.coupon.ticket_id > 0
      ? { ticket_id: raw.coupon.ticket_id }
      : {}),
    code: raw?.coupon?.code || '',
    detail: raw?.coupon?.detail || {},
    type: raw?.coupon?.type || '',
  },
  point: {
    applied: Boolean(raw?.point?.applied),
    source: (raw?.point?.source || 'none') as AppliedOfferSource,
    availablePoint: Number(raw?.point?.available_point || 0),
    maxExpendPoint: Number(raw?.point?.max_expend_point || 0),
    expendPoint: Number(raw?.point?.expend_point || 0),
    maxDiscount: Number(raw?.point?.max_discount || 0),
  },
  reason: raw?.reason || '',
});

/**
 * 接口文档
 * https://alidocs.dingtalk.com/i/nodes/QG53mjyd80R5bQdxUg3G49M1V6zbX04v
 */
export const buildCartAutoOfferParams = (state?: AutoOfferStore | null): AutoOfferApiParams => {
  // 小语种仅放行购物车显式开启的手动能力，不能误带自动优惠协议。
  // 默认 enabled 仍为 IS_US，MiniCart 等既有消费者不会因此开启小语种优惠。
  if (!state?.enabled || (!IS_US && state.couponPolicy !== 'manual')) return {};

  // 1/3 组走旧优惠协议，任何用户操作都不能重新开启 auto_apply_offer；2/4 组使用新协议。
  const automatic = state.couponPolicy === 'automatic';
  // 游客接口只允许码试算，不能携带券/积分，也不能因自动组而比较或选券。
  if (!state.authenticated) {
    return state.couponMode === 'manual_code' && state.code?.trim()
      ? { ...(automatic ? { auto_apply_offer: 1 as const } : {}), code: state.code.trim() }
      : {};
  }

  // 不传 auto_coupon_compare，避免后端比较最优券时替换用户的手动选择。
  const params: AutoOfferApiParams = automatic ? { auto_apply_offer: 1 } : {};
  switch (state.couponMode) {
    case 'auto':
      break;
    case 'skip':
      if (automatic) params.skip_coupon = 1;
      break;
    case 'manual_coupon':
      if (state.coupon) params.coupon = state.coupon;
      else if (automatic) params.skip_coupon = 1;
      break;
    case 'manual_code':
      if (state.code?.trim()) params.code = state.code.trim();
      else if (automatic) params.skip_coupon = 1;
      break;
  }

  // 用户主动开启最大可用积分时沿用旧协议：省略 point，由后端按当前选品计算可用积分。
  // 不强制传 1，避免取消最后一件可抵扣商品后仍要求使用积分而报 Available Points: 0。
  if (state.pointMode === 'auto') {
    return params;
  }
  // skip_coupon 只关闭券/码；关闭积分或无效的手动数量须显式传 0，不能触发后端默认积分。
  if (
    state.pointMode === 'manual' &&
    Number.isInteger(state.expendPoint) &&
    state.expendPoint > 0
  ) {
    params.point = 1;
    params.expend_point = state.expendPoint;
  } else {
    params.point = 0;
  }
  return params;
};

/** 参数构建不改变用户意图；购物车的新入场规则在恢复层处理，其他消费者沿用现有行为。 */
export const buildCartEnterAutoOfferParams = (state?: AutoOfferStore | null): AutoOfferApiParams =>
  buildCartAutoOfferParams(state);

/** 只回写展示结果；自动命中的券不能变成手动指定券，否则后续改商品会锁住旧券。 */
export const mergeAppliedOfferIntoState = (
  state: AutoOfferStore,
  rawAppliedOffer: any,
  legacyPricing?: Pick<ITotalData, 'code_discount' | 'point_discount'>,
): AutoOfferStore => {
  const applied_offer = normalizeAppliedOffer(rawAppliedOffer);

  // 手动组旧响应不含 applied_offer。仅依据成功报价的实际抵扣和明确意图回显，不能当作自动命中。
  if (state.couponPolicy === 'manual' && rawAppliedOffer == null && legacyPricing) {
    const couponApplied =
      hasManualCouponOrCodeSelection(state) &&
      Math.abs(normalizeAmount(legacyPricing.code_discount)) > 0;
    const pointApplied =
      state.pointMode !== 'none' && Math.abs(normalizeAmount(legacyPricing.point_discount)) > 0;
    applied_offer.legacy = true;
    applied_offer.applied = couponApplied;
    applied_offer.coupon.source = couponApplied ? 'manual' : 'none';
    applied_offer.coupon.type = state.couponMode === 'manual_code' ? 'code' : 'coupon';
    applied_offer.coupon.code = couponApplied ? state.code?.trim() || '' : '';
    applied_offer.point.applied = pointApplied;
    applied_offer.point.source = pointApplied ? 'manual' : 'none';
    // 旧响应只提供积分抵扣金额，不推算最大/已用积分数；可操作性由摘要独立处理。
  }

  return {
    ...state,
    applied_offer,
  };
};
